import { recoverTypedDataAddress, hashTypedData, keccak256, toBytes, isAddressEqual } from 'viem';

export const RULES = [
  ['signer', 'Signer'], ['document', 'Document'], ['class', 'Class'], ['window', 'Window'],
  ['one-time-use', 'One-time use'], ['not-revoked', 'Not revoked'], ['allowlist', 'Allowlist'],
];

const sameAddr = (a, b) => { try { return isAddressEqual(a, b); } catch { return false; } };

async function recover(domain, types, primaryType, message, signature) {
  try {
    return await recoverTypedDataAddress({ domain, types: { [primaryType]: types[primaryType] }, primaryType, message, signature });
  } catch {
    return null;
  }
}

async function checkRevocations(spec, m, digest, revocations) {
  for (const r of revocations || []) {
    const by = await recover(spec.domain, spec.types, 'ConsentRevocation', r.message, r.signature);
    if (by && sameAddr(by, m.principal) && sameAddr(r.message.principal, m.principal) && r.message.receiptDigest?.toLowerCase() === digest) {
      return [false, `Valid revocation signed by the principal at ${r.message.revokedAt}`];
    }
  }
  return [true, `${(revocations || []).length} revocation(s) supplied, none valid for this receipt`];
}

async function checkAllowlist(spec, m, ctx) {
  if (!ctx.allowlist) return [true, 'No allowlist supplied. Rule is optional.'];
  const { message: a, signature } = ctx.allowlist;
  const by = await recover(spec.domain, spec.types, 'PrincipalAllowlist', a, signature);
  if (!by || !sameAddr(by, ctx.operator || '')) return [false, `Allowlist not signed by the stated operator (recovered ${by || 'nothing'})`];
  if (a.agentId !== m.agentId) return [false, 'Allowlist is for a different agent'];
  const listed = (a.principals || []).some((p) => sameAddr(p, m.principal));
  return [listed, listed ? 'Principal is on the operator-signed allowlist' : 'Principal is not on the operator-signed allowlist'];
}

export async function checkReceipt(spec, { receipt, documentText, context }) {
  const m = receipt.message;
  const ctx = context || {};
  const t = Number(ctx.checkTime ?? Math.floor(Date.now() / 1000));
  const signer = await recover(spec.domain, spec.types, 'ConsentReceipt', m, receipt.signature);
  const digest = hashTypedData({ domain: spec.domain, types: { ConsentReceipt: spec.types.ConsentReceipt }, primaryType: 'ConsentReceipt', message: m }).toLowerCase();
  const presented = keccak256(toBytes(documentText ?? ''));
  const used = (ctx.usedNonces || []).map(String);
  const declared = Number(ctx.declaredClass ?? 3);
  const results = {
    signer: [!!signer && sameAddr(signer, m.principal), `Recovered ${signer || 'nothing'}`],
    document: [presented === String(m.documentHash).toLowerCase(), `Presented document hashes to ${presented}`],
    class: [Number(m.actionClass) === 3 && declared === 3, `actionClass ${m.actionClass}, declared class ${declared}`],
    window: [t >= Number(m.notBefore) && t < Number(m.expiry), `Check time ${t}, window ${m.notBefore} to ${m.expiry}`],
    'one-time-use': [!used.includes(String(m.nonce)), used.includes(String(m.nonce)) ? `Nonce ${m.nonce} already used` : `Nonce ${m.nonce} not seen before`],
    'not-revoked': await checkRevocations(spec, m, digest, ctx.revocations),
    allowlist: await checkAllowlist(spec, m, ctx),
  };
  const rules = RULES.map(([id, label]) => ({ id, label, ok: results[id][0], detail: results[id][1] }));
  const failed = rules.find((r) => !r.ok);
  return { valid: !failed, failedRule: failed?.id ?? null, digest, rules };
}
